import { useEffect, useState } from "react";
import { ArrowRight, Check, Minus, Quote, X } from "lucide-react";
import { useTheme } from "@exiftrator/shared/contexts";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@exiftrator/shared/components/ui";
import { GlassPanel, OpticBody, OpticButton, OpticEyebrow, OpticHeading, OpticSection } from "./OpticPrimitives";
import { resolveThemedScreenshot, ScreenshotCard } from "./ScreenshotCard";
import type {
  CarouselSection,
  CtaSection,
  ComparisonSection,
  FaqSection,
  HeroSection,
  HowItWorksSection,
  PricingSection,
  SpotlightSection,
  StatisticsSection,
  TestimonialsSection,
  OpticComparisonSupport,
} from "./landing.types";

type SectionProps<TSection> = {
  section: TSection;
  onCtaClick: (to: string) => void;
};

type CarouselApiLike = {
  selectedScrollSnap: () => number;
  scrollSnapList: () => number[];
  scrollTo: (index: number) => void;
  on: (eventName: "select" | "reInit", callback: () => void) => void;
  off: (eventName: "select" | "reInit", callback: () => void) => void;
};

function ComparisonMarker({ value, featured = false }: { value: OpticComparisonSupport; featured?: boolean }) {
  if (value === "partial") {
    return (
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-amber-500/20 text-amber-700 dark:bg-amber-300/20 dark:text-amber-200">
        <Minus className="size-4" />
      </span>
    );
  }

  if (value) {
    return (
      <span
        className={`inline-flex h-7 w-7 items-center justify-center rounded-full ${featured
          ? "bg-emerald-500/22 text-emerald-800 dark:bg-emerald-300/24 dark:text-emerald-200"
          : "bg-emerald-500/18 text-emerald-800 dark:bg-emerald-300/16 dark:text-emerald-200"
          }`}
      >
        <Check className="size-4" />
      </span>
    );
  }

  return (
    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-rose-500/20 text-rose-800 dark:bg-rose-300/20 dark:text-rose-200">
      <X className="size-4" />
    </span>
  );
}

export function HeroSectionBlock({
  section,
  onCtaClick,
  mounted,
}: SectionProps<HeroSection> & { mounted: boolean }) {
  const { theme } = useTheme();
  const screenshot = resolveThemedScreenshot(section.data.screenshot, theme);

  return (
    <OpticSection id={section.id} className="relative z-10 text-center">
      <div
        className={`transition-all duration-1000 ease-[cubic-bezier(0.25,1,0.5,1)] ${mounted ? "opacity-100 blur-0 translate-y-0" : "opacity-0 blur-xl translate-y-12"
          }`}
      >
        <h1 className="mx-auto max-w-[980px] text-balance text-5xl leading-[0.95] tracking-tight text-[#0f172a] dark:text-white md:text-7xl lg:text-[92px]">
          {section.data.headline}
          <br />
          <span className="font-serif italic text-[#0b704f] dark:text-[#4ade80]">{section.data.highlight}</span>
        </h1>
        <p className="mx-auto mt-8 max-w-[720px] text-base leading-relaxed text-[#334155] dark:text-[#94a3b8] md:text-xl">
          {section.data.description}
        </p>

        <div className="mt-12 flex flex-col items-center justify-center gap-4 sm:flex-row">
          <OpticButton onClick={() => onCtaClick(section.data.primaryCta.to)}>
            {section.data.primaryCta.label}
          </OpticButton>
          <OpticButton variant="ghost" onClick={() => onCtaClick(section.data.secondaryCta.to)}>
            {section.data.secondaryCta.label}
          </OpticButton>
        </div>
      </div>

      <div
        className={`mt-24 transition-all duration-[1200ms] delay-200 ease-[cubic-bezier(0.25,1,0.5,1)] ${mounted ? "opacity-100 blur-0 scale-100" : "opacity-0 blur-2xl scale-95"
          }`}
      >
        <GlassPanel className="relative overflow-hidden p-4 sm:p-6">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(74,222,128,0.12),transparent_40%),radial-gradient(circle_at_80%_80%,rgba(37,99,235,0.12),transparent_44%)]" />
          <div className="pointer-events-none absolute right-8 top-6 hidden font-mono text-[11px] uppercase tracking-[0.2em] text-[#64748b] dark:text-[#64748b] md:block">
            {section.data.frameLabel}
          </div>
          <div className="pointer-events-none absolute -left-8 bottom-0 hidden font-mono text-[104px] font-semibold tracking-tight text-[#0f172a]/5 dark:text-white/8 lg:block">
            {section.data.frameWatermark}
          </div>
          <ScreenshotCard screenshot={screenshot} title="Exiftrator editor" showOverlay={false} className="aspect-[16/9]" />
        </GlassPanel>
      </div>
    </OpticSection>
  );
}

export function SpotlightSectionBlock({ section }: SectionProps<SpotlightSection>) {
  const { theme } = useTheme();
  const headerAlignment = section.data.headerAlign ?? "left";

  return (
    <OpticSection id={section.id} className="mt-28">
      <div className={`mb-10 max-w-3xl ${headerAlignment === "center" ? "mx-auto text-center" : ""}`}>
        <OpticEyebrow>{section.data.eyebrow}</OpticEyebrow>
        <OpticHeading className="mt-3">{section.data.title}</OpticHeading>
        <OpticBody className="mt-3">{section.data.description}</OpticBody>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 md:gap-0">
        {section.data.items.map((item, index) => {
          const screenshot = item.screenshot ? resolveThemedScreenshot(item.screenshot, theme) : null;
          const isTopRow = index < 2;
          const isLeftColumn = index % 2 === 0;
          const hasNextItem = index < section.data.items.length - 1;

          return (
            <article
              key={item.title}
              className={`flex h-full flex-col py-8 md:py-8 ${isLeftColumn ? "md:pl-0 md:pr-8" : "md:pl-8 md:pr-0"} ${hasNextItem && !isTopRow ? "border-b border-[#1f2430]/15 dark:border-white/12 md:border-b-0" : ""
                } ${isTopRow ? "border-b border-[#1f2430]/15 dark:border-white/12" : ""} ${isLeftColumn ? "md:border-r md:border-[#1f2430]/15 md:dark:border-white/12" : ""
                }`}
            >
              <div className="flex h-full flex-col gap-4">
                <div>
                  <h3 className="text-xl tracking-tight text-[#0f172a] dark:text-white">{item.title}</h3>
                  <OpticBody className="mt-2 text-sm">{item.description}</OpticBody>
                </div>
                {screenshot ? (
                  <ScreenshotCard
                    screenshot={screenshot}
                    title={item.title}
                    showOverlay={false}
                    className="mt-auto aspect-square"
                  />
                ) : null}
              </div>
            </article>
          );
        })}
      </div>
    </OpticSection>
  );
}

export function StatisticsSectionBlock({ section }: SectionProps<StatisticsSection>) {
  return (
    <OpticSection id={section.id} className="mt-28">
      {section.data.eyebrow ? <OpticEyebrow>{section.data.eyebrow}</OpticEyebrow> : null}
      {section.data.title ? <OpticHeading className="mt-3">{section.data.title}</OpticHeading> : null}

      <div className="mt-10 overflow-x-auto">
        <div className="flex min-w-[760px]">
          {section.data.items.map((item, index) => (
            <div
              key={item.label}
              className={`flex-1 px-6 ${index === 0 ? "pl-0" : "border-l border-[#1f2430]/15 dark:border-white/12"} ${index === section.data.items.length - 1 ? "pr-0" : ""
                }`}
            >
              <p className="text-4xl font-semibold tracking-tight text-[#0f172a] dark:text-white">{item.value}</p>
              <p className="mt-1 text-base font-medium text-[#0f172a]/85 dark:text-[#dbe4f0]">{item.label}</p>
            </div>
          ))}
        </div>
      </div>
    </OpticSection>
  );
}

export function HowItWorksSectionBlock({ section }: SectionProps<HowItWorksSection>) {
  return (
    <OpticSection id={section.id} className="mt-28">
      <div className="mb-10 max-w-3xl">
        <OpticEyebrow>{section.data.eyebrow}</OpticEyebrow>
        <OpticHeading className="mt-3">{section.data.title}</OpticHeading>
        <OpticBody className="mt-3">{section.data.description}</OpticBody>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        {section.data.steps.map((step, index) => (
          <Card
            key={step.title}
            className="border-[#1f2430]/15 bg-white/70 dark:border-white/10 dark:bg-white/[0.03]"
          >
            <CardHeader>
              <div className="mb-3 inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#1f2430]/15 bg-[#0f172a]/6 font-mono text-xs font-semibold text-[#0f172a] dark:border-white/12 dark:bg-white/10 dark:text-white">
                {index + 1}
              </div>
              <CardTitle className="text-xl tracking-tight text-[#0f172a] dark:text-white">{step.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <OpticBody className="text-sm">{step.description}</OpticBody>
            </CardContent>
          </Card>
        ))}
      </div>
    </OpticSection>
  );
}

export function ProductCarouselSectionBlock({ section }: SectionProps<CarouselSection>) {
  const { theme } = useTheme();
  const [api, setApi] = useState<CarouselApiLike | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [snapCount, setSnapCount] = useState(0);

  useEffect(() => {
    if (!api) {
      return;
    }

    const onSelect = () => {
      setSelectedIndex(api.selectedScrollSnap());
      setSnapCount(api.scrollSnapList().length);
    };

    onSelect();
    api.on("select", onSelect);
    api.on("reInit", onSelect);

    return () => {
      api.off("select", onSelect);
      api.off("reInit", onSelect);
    };
  }, [api]);

  return (
    <OpticSection id={section.id} className="mt-28">
      <div className="mb-10 max-w-3xl">
        <OpticEyebrow>{section.data.eyebrow}</OpticEyebrow>
        <OpticHeading className="mt-3">{section.data.title}</OpticHeading>
        <OpticBody className="mt-3">{section.data.description}</OpticBody>
      </div>

      <div className="relative left-1/2 w-screen -translate-x-1/2">
        <Carousel
          setApi={(nextApi) => setApi(nextApi as unknown as CarouselApiLike)}
          opts={{ align: "center", containScroll: false, loop: false }}
          className="mx-auto w-full"
          aria-label="Exiftrator workflow carousel"
        >
          <CarouselContent className="!ml-0">
            {section.data.slides.map((slide) => (
              <CarouselItem key={slide.id} className="basis-[92%] !pl-0 px-4 sm:basis-[80%] lg:basis-[66%]">
                <div className="space-y-4">
                  <div className="text-center">
                    <p className="text-lg font-semibold tracking-tight text-[#0f172a] dark:text-white">{slide.title}</p>
                    <p className="mt-1 text-sm text-[#475569] dark:text-[#94a3b8]">{slide.description}</p>
                  </div>
                  <ScreenshotCard
                    screenshot={resolveThemedScreenshot(slide.screenshot, theme)}
                    title={slide.title}
                    showOverlay={false}
                    className="aspect-[16/9]"
                  />
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="left-2 border-[#1f2430]/20 bg-white/85 text-[#0f172a] hover:bg-white dark:border-white/15 dark:bg-black/65 dark:text-white dark:hover:bg-black/85 sm:left-6" />
          <CarouselNext className="right-2 border-[#1f2430]/20 bg-white/85 text-[#0f172a] hover:bg-white dark:border-white/15 dark:bg-black/65 dark:text-white dark:hover:bg-black/85 sm:right-6" />
        </Carousel>

        <div className="mt-6 flex items-center justify-center gap-2">
          {Array.from({ length: snapCount }).map((_, index) => (
            <Button
              key={`dot-${index}`}
              type="button"
              size="icon"
              variant="ghost"
              onClick={() => api?.scrollTo(index)}
              className={`h-2.5 w-2.5 rounded-full p-0 ${index === selectedIndex
                ? "bg-[#0f172a] hover:bg-[#0f172a] dark:bg-white dark:hover:bg-white"
                : "bg-[#0f172a]/25 hover:bg-[#0f172a]/45 dark:bg-white/25 dark:hover:bg-white/45"
                }`}
              aria-label={`Go to carousel slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </OpticSection>
  );
}

export function ComparisonSectionBlock({ section }: SectionProps<ComparisonSection>) {
  return (
    <OpticSection id={section.id} className="mt-28">
      <div className="mb-10 max-w-3xl">
        <OpticEyebrow>{section.data.eyebrow}</OpticEyebrow>
        <OpticHeading className="mt-3">{section.data.title}</OpticHeading>
        <OpticBody className="mt-3">{section.data.description}</OpticBody>
      </div>

      <div className="hidden overflow-x-auto rounded-2xl border border-[#1f2430]/15 bg-white/70 dark:border-white/10 dark:bg-white/[0.03] md:block">
        <Table className="min-w-[920px]">
          <TableHeader>
            <TableRow className="border-b-[#1f2430]/15 dark:border-b-white/10">
              <TableHead className="w-[270px] border-r border-[#1f2430]/12 text-[#0f172a] dark:border-white/10 dark:text-white">
                Metadata capability
              </TableHead>
              {section.data.columns.map((column) => (
                <TableHead
                  key={column.id}
                  className={`border-r border-[#1f2430]/12 text-center last:border-r-0 dark:border-white/10 ${column.featured ? "bg-[#0f172a]/4 dark:bg-white/5" : ""
                    }`}
                >
                  <p className={`text-sm font-semibold ${column.featured ? "text-[#0f172a] dark:text-white" : "text-[#334155] dark:text-[#cbd5e1]"}`}>
                    {column.name}
                  </p>
                  <p className="mt-1 text-[11px] font-medium text-[#64748b] dark:text-[#94a3b8]">{column.label}</p>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {section.data.rows.map((row) => (
              <TableRow key={row.feature} className="border-b-[#1f2430]/10 dark:border-b-white/7">
                <TableCell className="border-r border-[#1f2430]/12 text-sm font-medium text-[#0f172a] dark:border-white/10 dark:text-white">
                  {row.feature}
                </TableCell>
                {section.data.columns.map((column) => (
                  <TableCell
                    key={`${row.feature}-${column.id}`}
                    className={`border-r border-[#1f2430]/12 text-center last:border-r-0 dark:border-white/10 ${column.featured ? "bg-[#0f172a]/3 dark:bg-white/4" : ""
                      }`}
                  >
                    <ComparisonMarker value={row.support[column.id] ?? false} featured={column.featured} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="rounded-2xl border border-[#1f2430]/15 bg-white/70 p-4 dark:border-white/10 dark:bg-white/[0.03] md:hidden">
        <Accordion type="single" collapsible className="w-full">
          {section.data.rows.map((row) => (
            <AccordionItem key={row.feature} value={row.feature} className="border-b border-[#1f2430]/12 dark:border-white/10">
              <AccordionTrigger className="text-left text-sm font-medium text-[#0f172a] hover:no-underline dark:text-white">
                {row.feature}
              </AccordionTrigger>
              <AccordionContent className="space-y-2">
                {section.data.columns.map((column) => (
                  <div key={`${row.feature}-${column.id}`} className="flex items-center justify-between rounded-lg bg-[#0f172a]/4 px-3 py-2 dark:bg-white/7">
                    <div>
                      <p className="text-xs font-semibold text-[#0f172a] dark:text-white">{column.name}</p>
                      <p className="text-[11px] text-[#64748b] dark:text-[#94a3b8]">{column.label}</p>
                    </div>
                    <ComparisonMarker value={row.support[column.id] ?? false} featured={column.featured} />
                  </div>
                ))}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>

      {section.data.footnote ? <p className="mt-4 text-xs text-[#64748b] dark:text-[#94a3b8]">{section.data.footnote}</p> : null}
    </OpticSection>
  );
}

export function TestimonialsSectionBlock({ section }: SectionProps<TestimonialsSection>) {
  return (
    <OpticSection id={section.id} className="mt-28">
      <div className="mb-10 max-w-3xl">
        <OpticEyebrow>{section.data.eyebrow}</OpticEyebrow>
        <OpticHeading className="mt-3">{section.data.title}</OpticHeading>
        <OpticBody className="mt-3">{section.data.description}</OpticBody>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {section.data.items.map((item) => (
          <Card key={`${item.persona}-${item.context}`} className="border-[#1f2430]/15 bg-white/70 dark:border-white/10 dark:bg-white/[0.03]">
            <CardHeader className="space-y-3">
              <Quote className="size-5 text-[#0f172a]/55 dark:text-white/55" />
              <CardTitle className="text-base font-medium leading-relaxed text-[#0f172a] dark:text-white">“{item.quote}”</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm font-semibold text-[#0f172a] dark:text-white">{item.persona}</p>
              <p className="text-xs text-[#64748b] dark:text-[#94a3b8]">
                {item.role} • {item.context}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {section.data.note ? (
        <p className="mt-4 text-xs leading-relaxed text-[#64748b] dark:text-[#94a3b8]">{section.data.note}</p>
      ) : null}
    </OpticSection>
  );
}

export function PricingSectionBlock({ section, onCtaClick }: SectionProps<PricingSection>) {
  return (
    <OpticSection id={section.id} className="mt-28">
      <div className="mb-10 max-w-3xl">
        <OpticEyebrow>{section.data.eyebrow}</OpticEyebrow>
        <OpticHeading className="mt-3">{section.data.title}</OpticHeading>
        <OpticBody className="mt-3">{section.data.description}</OpticBody>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {section.data.tiers.map((tier) => (
          <Card
            key={tier.id}
            className={`flex h-full flex-col border ${tier.highlighted
              ? "border-[#0f172a]/35 bg-[#0f172a]/6 dark:border-white/30 dark:bg-white/6"
              : "border-[#1f2430]/15 bg-white/70 dark:border-white/10 dark:bg-white/[0.03]"
              }`}
          >
            <CardHeader>
              {tier.badge ? (
                <Badge className="mb-3 w-fit rounded-full bg-[#0f172a]/8 text-[#0f172a] hover:bg-[#0f172a]/8 dark:bg-white/12 dark:text-white">
                  {tier.badge}
                </Badge>
              ) : null}
              <CardTitle className="text-2xl tracking-tight text-[#0f172a] dark:text-white">{tier.name}</CardTitle>
              <div className="mt-2 flex items-end gap-2">
                <span className="text-4xl font-semibold tracking-tight text-[#0f172a] dark:text-white">{tier.price}</span>
                <span className="pb-1 text-sm text-[#64748b] dark:text-[#94a3b8]">{tier.period}</span>
              </div>
              <OpticBody className="mt-3 text-sm">{tier.description}</OpticBody>
            </CardHeader>
            <CardContent className="flex h-full flex-col">
              <ul className="mb-8 space-y-2">
                {tier.features.map((feature) => (
                  <li key={`${tier.id}-${feature}`} className="flex items-start gap-2 text-sm text-[#334155] dark:text-[#94a3b8]">
                    <Check className="mt-0.5 size-4 shrink-0 text-[#0f172a] dark:text-[#4ade80]" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <OpticButton className="mt-8 w-full justify-center lg:mt-auto" onClick={() => onCtaClick(tier.cta.to)}>
                {tier.cta.label}
                <ArrowRight className="ml-2 size-3.5" />
              </OpticButton>
            </CardContent>
          </Card>
        ))}
      </div>
    </OpticSection>
  );
}

export function FaqSectionBlock({ section }: SectionProps<FaqSection>) {
  return (
    <OpticSection id={section.id} className="mt-28">
      <GlassPanel className="p-6 sm:p-8">
        <OpticEyebrow>{section.data.eyebrow}</OpticEyebrow>
        <OpticHeading className="mt-3">{section.data.title}</OpticHeading>
        <OpticBody className="mt-3">{section.data.description}</OpticBody>

        <Accordion type="single" collapsible className="mt-8 w-full">
          {section.data.faqs.map((faq) => (
            <AccordionItem key={faq.question} value={faq.question} className="border-b border-[#1f2430]/12 dark:border-white/10">
              <AccordionTrigger className="text-left text-sm font-medium text-[#0f172a] hover:no-underline dark:text-white">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-sm leading-relaxed text-[#334155] dark:text-[#94a3b8]">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </GlassPanel>
    </OpticSection>
  );
}

export function CtaSectionBlock({ section, onCtaClick }: SectionProps<CtaSection>) {
  return (
    <OpticSection id={section.id} className="mt-28 pb-6">
      <GlassPanel className="mx-auto max-w-3xl p-6 text-center sm:p-8">
        <OpticEyebrow>{section.data.eyebrow}</OpticEyebrow>
        <h3 className="mt-3 text-3xl tracking-tight text-[#0f172a] dark:text-white sm:text-4xl">{section.data.title}</h3>
        <OpticBody className="mx-auto mt-3 max-w-2xl">{section.data.description}</OpticBody>

        <OpticButton className="mt-8 justify-center" onClick={() => onCtaClick(section.data.button.to)}>
          {section.data.button.label}
          <ArrowRight className="ml-2 size-3.5" />
        </OpticButton>
      </GlassPanel>
    </OpticSection>
  );
}
